import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, TrendingUp, TrendingDown, PieChart, 
  Utensils, Car, ShoppingBag, Home, Zap, Sparkles,
  ChevronRight, Lightbulb, PiggyBank, MapPin, Clock,
  ArrowUpRight, ArrowDownLeft, IndianRupee, Calendar,
  Target, Bell, Repeat, CreditCard,
  Wifi, Smartphone, Play, ChevronDown, Brain
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const SpendsPage = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAllBills, setShowAllBills] = useState(false);

  // Current date info for calculations
  const today = new Date();
  const currentDay = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const remainingDays = daysInMonth - currentDay;

  // Spend categories with colors and icons
  const categories = [
    { id: 'food', name: 'Food & Dining', icon: Utensils, amount: 8450, percentage: 32, color: '#FF6B6B', trend: '+12%', avgTransaction: 380 },
    { id: 'travel', name: 'Travel', icon: Car, amount: 6200, percentage: 24, color: '#4ECDC4', trend: '+18%', avgTransaction: 450 },
    { id: 'shopping', name: 'Shopping', icon: ShoppingBag, amount: 5800, percentage: 22, color: '#9B59B6', trend: '-5%', avgTransaction: 1200 },
    { id: 'bills', name: 'Bills & Utilities', icon: Zap, amount: 3200, percentage: 12, color: '#F39C12', trend: '0%', avgTransaction: 1500 },
    { id: 'others', name: 'Others', icon: Home, amount: 2650, percentage: 10, color: '#95A5A6', trend: '+3%', avgTransaction: 500 }
  ];

  const totalSpend = categories.reduce((sum, cat) => sum + cat.amount, 0);
  const monthlyBudget = 35000; // User's budget (could be from settings)

  // ============================================
  // AI FEATURE 1: MONTH-END FORECAST
  // ============================================
  const monthEndForecast = useMemo(() => {
    const dailyAverage = totalSpend / currentDay;
    const projectedTotal = Math.round(dailyAverage * daysInMonth);
    const percentOfBudget = Math.round((projectedTotal / monthlyBudget) * 100);
    const overBudget = projectedTotal > monthlyBudget;
    const difference = Math.abs(projectedTotal - monthlyBudget);
    
    return {
      projectedTotal,
      dailyAverage: Math.round(dailyAverage),
      percentOfBudget,
      overBudget,
      difference,
      remainingDays,
      safeSpendPerDay: Math.max(0, Math.round((monthlyBudget - totalSpend) / remainingDays))
    };
  }, [totalSpend, currentDay, daysInMonth, monthlyBudget, remainingDays]);

  // ============================================
  // AI FEATURE 2: RECURRING BILL PREDICTOR
  // ============================================
  const predictedBills = useMemo(() => {
    // Detected recurring patterns (in real app, ML would detect these)
    const bills = [
      {
        id: 'bill1',
        name: 'Electricity Bill',
        icon: Zap,
        amount: 1450,
        dueIn: 3,
        dueDate: 'Dec 18',
        confidence: 95,
        lastPaid: 'Nov 15 - ₹1,380',
        trend: '+5%',
        category: 'bills'
      },
      {
        id: 'bill2',
        name: 'Netflix',
        icon: Play,
        amount: 649,
        dueIn: 7,
        dueDate: 'Dec 22',
        confidence: 99,
        lastPaid: 'Nov 22 - ₹649',
        trend: '0%',
        category: 'entertainment'
      },
      {
        id: 'bill3',
        name: 'Mobile Recharge',
        icon: Smartphone,
        amount: 599,
        dueIn: 12,
        dueDate: 'Dec 27',
        confidence: 90,
        lastPaid: 'Nov 27 - ₹599',
        trend: '0%',
        category: 'bills'
      },
      {
        id: 'bill4',
        name: 'WiFi / Broadband',
        icon: Wifi,
        amount: 999,
        dueIn: 15,
        dueDate: 'Dec 30',
        confidence: 98,
        lastPaid: 'Nov 30 - ₹999',
        trend: '0%',
        category: 'bills'
      },
      {
        id: 'bill5',
        name: 'Credit Card Bill',
        icon: CreditCard,
        amount: 15000,
        dueIn: 18,
        dueDate: 'Jan 2',
        confidence: 85,
        lastPaid: 'Dec 2 - ₹12,500',
        trend: '+20%',
        category: 'bills'
      }
    ];
    
    return bills.sort((a, b) => a.dueIn - b.dueIn);
  }, []);

  const totalUpcomingBills = predictedBills.reduce((sum, bill) => sum + bill.amount, 0);
  const visibleBills = showAllBills ? predictedBills : predictedBills.slice(0, 3);

  // Smart insights - only 2-3 max, very focused
  const insights = [
    {
      id: 1,
      type: 'observation',
      icon: TrendingUp,
      iconColor: '#4ECDC4',
      text: 'Travel spending increased by 18% compared to last month.',
      subtext: '₹6,200 this month vs ₹5,254 last month'
    },
    {
      id: 2,
      type: 'observation',
      icon: Utensils,
      iconColor: '#FF6B6B',
      text: 'You spent ₹8,450 on food & dining this month.',
      subtext: '₹4,200 on restaurants, ₹4,250 on delivery apps'
    }
  ];

  // Save suggestions - calm, respectful, one per category
  const saveSuggestions = [
    {
      id: 1,
      category: 'Travel',
      icon: Car,
      color: '#4ECDC4',
      title: 'Save on Travel',
      description: 'You frequently travel Bangalore → Chennai.',
      suggestion: 'Similar bus options available at 15-20% lower average cost.',
      potentialSaving: '₹800-1,200/month',
      action: 'View Options'
    },
    {
      id: 2,
      category: 'Food',
      icon: Utensils,
      color: '#FF6B6B',
      title: 'Food Delivery Tip',
      description: 'High delivery fees on frequent orders.',
      suggestion: 'Ordering from nearby restaurants could save ~₹600/month in delivery fees.',
      potentialSaving: '₹500-700/month',
      action: 'See Nearby'
    }
  ];

  // Recent category transactions
  const recentTransactions = [
    { id: 1, name: 'Swiggy', category: 'food', amount: 450, date: 'Today, 1:30 PM', type: 'debit' },
    { id: 2, name: 'Uber', category: 'travel', amount: 280, date: 'Today, 9:15 AM', type: 'debit' },
    { id: 3, name: 'Amazon', category: 'shopping', amount: 1299, date: 'Yesterday', type: 'debit' },
    { id: 4, name: 'Electricity Bill', category: 'bills', amount: 1450, date: 'Yesterday', type: 'debit' },
    { id: 5, name: 'Zomato', category: 'food', amount: 380, date: '2 days ago', type: 'debit' }
  ];

  // Donut chart segments calculation
  const generateDonutPath = (percentage, startAngle, color) => {
    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const strokeLength = (percentage / 100) * circumference;
    const strokeDasharray = `${strokeLength} ${circumference - strokeLength}`;
    const strokeDashoffset = -((startAngle / 360) * circumference);
    
    return {
      strokeDasharray,
      strokeDashoffset,
      stroke: color
    };
  };

  let currentAngle = 0;
  const donutSegments = categories.map(cat => {
    const segment = {
      ...cat,
      startAngle: currentAngle,
      ...generateDonutPath(cat.percentage, currentAngle, cat.color)
    };
    currentAngle += (cat.percentage / 100) * 360;
    return segment;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#586BFF] to-[#8B5CF6] text-white p-5 pb-8">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Spends</h1>
            <p className="text-sm text-white/80">Understand where your money goes</p>
          </div>
          <div className="flex items-center gap-1 bg-white/20 px-2 py-1 rounded-full">
            <Brain className="w-4 h-4" />
            <span className="text-xs font-medium">AI</span>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 bg-white/10 rounded-xl p-1">
          {['week', 'month', 'year'].map(period => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all capitalize ${
                selectedPeriod === period 
                  ? 'bg-white text-[#586BFF]' 
                  : 'text-white/80 hover:bg-white/10'
              }`}
            >
              This {period}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 -mt-4">
        {/* ============================================ */}
        {/* SPEND BREAKDOWN - KEPT AT TOP */}
        {/* ============================================ */}
        <Card className="p-5 mb-5 shadow-lg border-0 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-500">Total Spent</p>
              <p className="text-3xl font-bold text-gray-800">₹{totalSpend.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">This {selectedPeriod}</p>
            </div>
            
            {/* Donut Chart */}
            <div className="relative w-28 h-28">
              <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
                {donutSegments.map((segment, idx) => (
                  <motion.circle
                    key={segment.id}
                    cx="100"
                    cy="100"
                    r="80"
                    fill="none"
                    strokeWidth="24"
                    stroke={segment.color}
                    strokeDasharray={segment.strokeDasharray}
                    strokeDashoffset={segment.strokeDashoffset}
                    strokeLinecap="round"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.1 }}
                    className="cursor-pointer hover:opacity-80"
                    onClick={() => setSelectedCategory(selectedCategory === segment.id ? null : segment.id)}
                  />
                ))}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <PieChart className="w-6 h-6 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Category Breakdown - Horizontal bars */}
          <div className="space-y-3">
            {categories.map((cat, idx) => {
              const Icon = cat.icon;
              const isSelected = selectedCategory === cat.id;
              return (
                <motion.div 
                  key={cat.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  onClick={() => setSelectedCategory(isSelected ? null : cat.id)}
                  className={`cursor-pointer rounded-xl p-3 transition-all ${
                    isSelected ? 'bg-gray-100 ring-2 ring-offset-2' : 'hover:bg-gray-50'
                  }`}
                  style={{ ringColor: isSelected ? cat.color : 'transparent' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${cat.color}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: cat.color }} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{cat.name}</p>
                        <p className="text-xs text-gray-500">{cat.percentage}% of total</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-800">₹{cat.amount.toLocaleString()}</p>
                      <p className={`text-xs font-medium ${
                        cat.trend.startsWith('+') ? 'text-red-500' : 
                        cat.trend.startsWith('-') ? 'text-green-500' : 'text-gray-500'
                      }`}>
                        {cat.trend} vs last {selectedPeriod}
                      </p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full rounded-full"
                      style={{ backgroundColor: cat.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${cat.percentage}%` }}
                      transition={{ delay: idx * 0.1, duration: 0.5 }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Card>

        {/* Recent Transactions in Selected Category */}
        <AnimatePresence>
          {selectedCategory && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-5"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-gray-800">
                  {categories.find(c => c.id === selectedCategory)?.name} Transactions
                </h3>
                <button 
                  onClick={() => setSelectedCategory(null)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              </div>
              
              <Card className="divide-y divide-gray-100 border-0 shadow-md overflow-hidden">
                {recentTransactions
                  .filter(t => t.category === selectedCategory)
                  .map((txn, idx) => (
                    <motion.div 
                      key={txn.id}
                      className="p-3 flex items-center justify-between hover:bg-gray-50"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                          <ArrowUpRight className="w-5 h-5 text-red-500" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800 text-sm">{txn.name}</p>
                          <p className="text-xs text-gray-500">{txn.date}</p>
                        </div>
                      </div>
                      <p className="font-bold text-gray-800">-₹{txn.amount}</p>
                    </motion.div>
                  ))}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Smart Insights Section - Clean & Minimal */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-gray-800">Insights</h3>
          </div>
          
          <div className="space-y-3">
            {insights.map((insight, idx) => {
              const Icon = insight.icon;
              return (
                <motion.div
                  key={insight.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + idx * 0.1 }}
                >
                  <Card className="p-4 border-0 shadow-md bg-gradient-to-r from-white to-gray-50">
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${insight.iconColor}20` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: insight.iconColor }} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-800 font-medium">{insight.text}</p>
                        <p className="text-xs text-gray-500 mt-1">{insight.subtext}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Save Suggestions - The Key Feature */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <PiggyBank className="w-5 h-5 text-green-500" />
            <h3 className="font-bold text-gray-800">Ways to Save</h3>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Smart</span>
          </div>

          <div className="space-y-3">
            {saveSuggestions.map((suggestion, idx) => {
              const Icon = suggestion.icon;
              return (
                <motion.div
                  key={suggestion.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + idx * 0.1 }}
                >
                  <Card className="p-4 border-0 shadow-md overflow-hidden relative">
                    {/* Subtle gradient accent */}
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-1"
                      style={{ backgroundColor: suggestion.color }}
                    />
                    
                    <div className="flex items-start gap-3 pl-2">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${suggestion.color}15` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: suggestion.color }} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-bold text-gray-800">{suggestion.title}</h4>
                          <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                            Save {suggestion.potentialSaving}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{suggestion.description}</p>
                        <p className="text-xs text-gray-500 mb-3">{suggestion.suggestion}</p>
                        
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-8 text-xs"
                          style={{ borderColor: suggestion.color, color: suggestion.color }}
                        >
                          {suggestion.action}
                          <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Monthly Comparison */}
        <Card className="p-5 mb-5 border-0 shadow-md bg-gradient-to-br from-[#586BFF]/5 to-[#8B5CF6]/5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#586BFF]" />
              <h3 className="font-bold text-gray-800">vs Last Month</h3>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 bg-white rounded-xl">
              <p className="text-2xl font-bold text-gray-800">₹{(totalSpend / 1000).toFixed(1)}K</p>
              <p className="text-xs text-gray-500">This month</p>
            </div>
            <div className="text-center p-3 bg-white rounded-xl">
              <p className="text-2xl font-bold text-gray-800">₹{((totalSpend * 0.92) / 1000).toFixed(1)}K</p>
              <p className="text-xs text-gray-500">Last month</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-xl">
              <p className="text-2xl font-bold text-red-600">+8%</p>
              <p className="text-xs text-red-600">Increase</p>
            </div>
          </div>
        </Card>

        {/* ============================================ */}
        {/* AI FEATURES - MOVED TO END */}
        {/* ============================================ */}
        
        {/* AI Section Header */}
        <div className="flex items-center gap-2 mb-4 mt-6">
          <div className="flex items-center gap-1 bg-gradient-to-r from-[#586BFF] to-[#8B5CF6] text-white px-3 py-1 rounded-full">
            <Brain className="w-4 h-4" />
            <span className="text-xs font-semibold">AI Powered</span>
          </div>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* AI FEATURE 1: MONTH-END FORECAST */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-4"
        >
          <Card className={`p-5 shadow-lg border-0 ${
            monthEndForecast.overBudget 
              ? 'bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-l-red-500' 
              : 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-l-green-500'
          }`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  monthEndForecast.overBudget ? 'bg-red-100' : 'bg-green-100'
                }`}>
                  <Target className={`w-5 h-5 ${monthEndForecast.overBudget ? 'text-red-600' : 'text-green-600'}`} />
                </div>
                <div>
                  <p className="font-bold text-gray-800 flex items-center gap-1">
                    Month-End Forecast
                    <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full font-semibold">AI</span>
                  </p>
                  <p className="text-xs text-gray-500">{remainingDays} days remaining</p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-1">At current pace, you'll spend:</p>
              <p className={`text-3xl font-bold ${monthEndForecast.overBudget ? 'text-red-600' : 'text-green-600'}`}>
                ₹{monthEndForecast.projectedTotal.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">by Dec 31</p>
            </div>

            {/* Budget Progress Bar */}
            <div className="mb-3">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-gray-600">Budget: ₹{monthlyBudget.toLocaleString()}</span>
                <span className={monthEndForecast.overBudget ? 'text-red-600 font-semibold' : 'text-green-600 font-semibold'}>
                  {monthEndForecast.percentOfBudget}%
                </span>
              </div>
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <motion.div 
                  className={`h-full rounded-full ${monthEndForecast.overBudget ? 'bg-red-500' : 'bg-green-500'}`}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(monthEndForecast.percentOfBudget, 100)}%` }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                />
              </div>
            </div>

            {/* Actionable Insight */}
            <div className={`p-3 rounded-xl ${monthEndForecast.overBudget ? 'bg-red-100/50' : 'bg-green-100/50'}`}>
              {monthEndForecast.overBudget ? (
                <p className="text-sm text-red-700">
                  <span className="font-semibold">⚠️ Over budget by ₹{monthEndForecast.difference.toLocaleString()}</span>
                  <br />
                  <span className="text-xs">To stay on budget, limit daily spending to ₹{monthEndForecast.safeSpendPerDay.toLocaleString()}</span>
                </p>
              ) : (
                <p className="text-sm text-green-700">
                  <span className="font-semibold">✓ On track!</span>
                  <br />
                  <span className="text-xs">You can safely spend ₹{monthEndForecast.safeSpendPerDay.toLocaleString()}/day for the rest of the month</span>
                </p>
              )}
            </div>
          </Card>
        </motion.div>

        {/* AI FEATURE 2: RECURRING BILL PREDICTOR */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="mb-5"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-500" />
              <h3 className="font-bold text-gray-800">Upcoming Bills</h3>
              <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-semibold">Auto-detected</span>
            </div>
            <p className="text-sm font-semibold text-gray-600">₹{totalUpcomingBills.toLocaleString()}</p>
          </div>

          <Card className="p-4 shadow-lg border-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50">
            <div className="space-y-3">
              {visibleBills.map((bill, idx) => {
                const Icon = bill.icon;
                const isUrgent = bill.dueIn <= 3;
                return (
                  <motion.div
                    key={bill.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 + idx * 0.05 }}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                      isUrgent ? 'bg-red-50 border border-red-200' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      isUrgent ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-blue-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800 text-sm truncate">{bill.name}</p>
                        {isUrgent && (
                          <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-semibold animate-pulse">
                            Due Soon!
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {isUrgent ? `Due in ${bill.dueIn} days` : bill.dueDate} • {bill.confidence}% confident
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${isUrgent ? 'text-red-600' : 'text-gray-800'}`}>
                        ₹{bill.amount.toLocaleString()}
                      </p>
                      {bill.trend !== '0%' && (
                        <p className={`text-[10px] ${
                          bill.trend.startsWith('+') ? 'text-red-500' : 'text-green-500'
                        }`}>
                          {bill.trend} vs last
                        </p>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {predictedBills.length > 3 && (
              <button
                onClick={() => setShowAllBills(!showAllBills)}
                className="w-full mt-3 py-2 text-sm text-blue-600 font-medium flex items-center justify-center gap-1 hover:bg-blue-50 rounded-lg transition-colors"
              >
                {showAllBills ? 'Show Less' : `Show All ${predictedBills.length} Bills`}
                <ChevronDown className={`w-4 h-4 transition-transform ${showAllBills ? 'rotate-180' : ''}`} />
              </button>
            )}

            {/* Upcoming Bills Summary */}
            <div className="mt-4 p-3 bg-blue-100/50 rounded-xl">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">💡 Plan ahead:</span> You have ₹{totalUpcomingBills.toLocaleString()} in bills coming up. 
                {monthEndForecast.safeSpendPerDay > 0 && (
                  <span> Keep aside ₹{Math.round(totalUpcomingBills / remainingDays).toLocaleString()}/day to cover them.</span>
                )}
              </p>
            </div>
          </Card>
        </motion.div>

        {/* Calm footer message */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-400">
            🔒 Insights update as you transact. Your data stays private.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SpendsPage;